import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Link2, Plus, Ban, QrCode } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Spinner } from '../../components/ui/Spinner';
import {
  inviteExpiryDaysError,
  inviteMaxUsesError,
} from '../../lib/leadBot/testerInvite';
import {
  DEFAULT_INVITE_EXPIRY_DAYS,
  DEFAULT_INVITE_MAX_USES,
  type BotTesterInviteRow,
} from '../../types/botTesterInvite';
import { createTesterInvite, listTesterInvites, revokeTesterInvite } from './api';
import { InviteQr } from './InviteQr';
import { DEMO_INVITE_MAX_EXPIRY_DAYS, DEMO_INVITE_MAX_USES } from '../../lib/leadBot/demoMode';

// SMS Lead Bot — issuing tester invite links, in bot settings.
//
// ⚠️⚠️ THE COPY HERE EXPLAINS A DECISION, not a feature. The ask was a public
// page that anyone could fill in; what exists is a link kyck issues. Whoever
// reads this screen is the person who might wonder why they cannot just publish
// a URL, so the answer is on it: the allowlist is the guard on autonomous
// sending, and a public form would hand that guard to the internet.
//
// ⚠️ Issuing a link does NOT loosen anything on its own. A number only reaches
// the allowlist when a tester redeems, and everything downstream — autonomous
// sending needing a non-empty allowlist, inbound routing admitting only
// allowlisted numbers — is untouched.

function toNumber(raw: string): number {
  const trimmed = raw.trim();
  // ⚠️ Blank is NaN, never 0 — the toCapNumber trap, for the same reason: 0
  // here would read as "unlimited uses", which is the one value that must not
  // be storable.
  return trimmed === '' ? Number.NaN : Number(trimmed);
}

function stateBadge(row: BotTesterInviteRow) {
  switch (row.state) {
    case 'ok':
      return <Badge variant="success">Live</Badge>;
    case 'revoked':
      return <Badge variant="neutral">Turned off</Badge>;
    case 'expired':
      return <Badge variant="neutral">Expired</Badge>;
    case 'used_up':
    default:
      return <Badge variant="neutral">Used up</Badge>;
  }
}

function expiryIsoFromDays(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString();
}

function formatDate(iso: string): string {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return '—';
  return new Date(ms).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

export function TesterInviteSection({ botConfigId, isDemo = false }: { botConfigId: string; isDemo?: boolean }) {
  const queryClient = useQueryClient();
  const [label, setLabel] = useState('');
  // A DEMO bot issues conference links: up to 600 uses, but only for 5 days.
  const usesCeiling = isDemo ? DEMO_INVITE_MAX_USES : undefined;
  const expiryCeiling = isDemo ? DEMO_INVITE_MAX_EXPIRY_DAYS : undefined;
  const [maxUses, setMaxUses] = useState(String(isDemo ? DEMO_INVITE_MAX_USES : DEFAULT_INVITE_MAX_USES));
  const [expiryDays, setExpiryDays] = useState(String(isDemo ? DEMO_INVITE_MAX_EXPIRY_DAYS : DEFAULT_INVITE_EXPIRY_DAYS));
  const [qrFor, setQrFor] = useState<string | null>(null);

  const { data: invites = [], isLoading } = useQuery({
    queryKey: ['lead-bot', 'invites', botConfigId],
    queryFn: () => listTesterInvites(botConfigId),
  });

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ['lead-bot', 'invites', botConfigId] });

  // ⚠️ The SAME validators the API runs, so the form cannot offer to create a
  // link the server will refuse.
  const usesError = inviteMaxUsesError(toNumber(maxUses), usesCeiling);
  const expiryError = inviteExpiryDaysError(toNumber(expiryDays), expiryCeiling);
  const canCreate = label.trim() !== '' && usesError === null && expiryError === null;

  const create = useMutation({
    mutationFn: () =>
      createTesterInvite({
        botConfigId,
        label: label.trim(),
        maxUses: toNumber(maxUses),
        expiresAt: expiryIsoFromDays(toNumber(expiryDays)),
      }),
    onSuccess: async (invite) => {
      setLabel('');
      invalidate();
      // ⚠️ THE LINK IS THE DELIVERABLE, so it goes straight to the clipboard —
      // the hosted-report share posture. A created link nobody copied is a
      // created link nobody used.
      try {
        await navigator.clipboard.writeText(invite.url);
        toast.success('Link created and copied to your clipboard.');
      } catch {
        toast.success('Link created — use Copy to grab it.');
      }
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => revokeTesterInvite(id),
    onSuccess: () => {
      invalidate();
      toast.success('That link will no longer work.');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const copy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied.');
    } catch {
      toast.error('Could not copy — select the link and copy it by hand.');
    }
  };

  return (
    <div className="space-y-4">
      {/* ⚠️ WHY A LINK RATHER THAN A PUBLIC PAGE, stated where someone would
          otherwise ask for the public page. */}
      <p className="text-sm text-muted-foreground">
        Send a tester a link and they enter their own mobile — you never type phone numbers.{' '}
        <strong>The link is the permission</strong>: only someone you send it to can start a
        conversation. There is no public sign-up page on purpose, because anyone who found it
        could have us text a stranger on the client's behalf.
      </p>

      <div className="grid gap-3 sm:grid-cols-[1fr_8rem_8rem_auto] sm:items-end">
        <div>
          <Label htmlFor="invite-label">Who is this link for?</Label>
          <Input
            id="invite-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Camperagent sales team"
          />
        </div>
        <div>
          <Label htmlFor="invite-uses">How many testers</Label>
          <Input
            id="invite-uses"
            type="number"
            min={1}
            value={maxUses}
            onChange={(e) => setMaxUses(e.target.value)}
            aria-invalid={usesError !== null}
          />
        </div>
        <div>
          <Label htmlFor="invite-expiry">Expires in (days)</Label>
          <Input
            id="invite-expiry"
            type="number"
            min={1}
            value={expiryDays}
            onChange={(e) => setExpiryDays(e.target.value)}
            aria-invalid={expiryError !== null}
          />
        </div>
        <Button onClick={() => create.mutate()} disabled={!canCreate || create.isPending}>
          {create.isPending ? <Spinner className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
          Create link
        </Button>
      </div>

      {(usesError || expiryError) && (
        <p className="text-xs text-rose-600">{usesError ?? expiryError}</p>
      )}

      {isLoading ? (
        <Spinner />
      ) : invites.length === 0 ? (
        <p className="text-sm text-muted-foreground">No links yet.</p>
      ) : (
        <div className="space-y-2">
          {invites.map((invite) => (
            <div
              key={invite.id}
              className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-3"
            >
              <div className="min-w-0">
                <p className="flex items-center gap-2 text-sm font-medium">
                  <Link2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="truncate">{invite.label}</span>
                  {stateBadge(invite)}
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {invite.usedCount} of {invite.maxUses} used · expires{' '}
                  {formatDate(invite.expiresAt)}
                  {invite.createdByName ? ` · created by ${invite.createdByName}` : ''}
                </p>
              </div>
              <div className="flex gap-2">
                {/* ⚠️ Copy stays available on a dead link so an AM can see WHAT
                    they sent someone who is asking why it doesn't work. */}
                <Button variant="outline" size="sm" onClick={() => void copy(invite.url)}>
                  <Copy className="h-4 w-4" />
                  Copy
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setQrFor(qrFor === invite.id ? null : invite.id)}
                >
                  <QrCode className="h-4 w-4" />
                  QR
                </Button>
                {invite.state !== 'revoked' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => revoke.mutate(invite.id)}
                    disabled={revoke.isPending}
                  >
                    <Ban className="h-4 w-4" />
                    Turn off
                  </Button>
                )}
              </div>
              {qrFor === invite.id ? (
                <div className="w-full">
                  <InviteQr url={invite.url} label={invite.label} />
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}

      {/* ⚠️ Says what revoking does and does NOT do — someone will reach for it
          expecting it to remove a tester from the allowlist. */}
      <p className="text-xs text-muted-foreground">
        Turning a link off stops anyone new using it. Testers who already signed up stay on the
        allowlist — remove them there.
      </p>
    </div>
  );
}
